/**
 * BellButton — opens the activity drawer. Shows an unread-count pip
 * derived from the latest activity row's `ts` vs
 * `localStorage['tt:activity:lastSeenAt']`.
 */
import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { getCharacterActivity, type ActivityRow } from '../../lib/api';
import { ActivityDrawer } from './ActivityDrawer';

const SEEN_KEY = 'tt:activity:lastSeenAt';

interface Props {
  charId: string;
  /** Bumping this causes the bell to re-poll the server (e.g. after a
   *  round end has just written new activity rows). */
  refreshKey?: number | string;
}

export function BellButton({ charId, refreshKey }: Props) {
  const [items, setItems] = useState<ActivityRow[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const out = await getCharacterActivity(charId, 50);
        if (!live) return;
        setItems(out.items ?? []);
      } catch { /* */ }
    })();
    return () => { live = false; };
  }, [charId, refreshKey]);

  const lastSeen = readLastSeen();
  const unreadCount = items.filter((i) => i.ts > lastSeen).length;

  function openDrawer() {
    setOpen(true);
    if (items.length > 0) writeLastSeen(items[0]!.ts);
  }

  return (
    <>
      <button
        type="button"
        className={`bell-button${unreadCount > 0 ? ' has-unread' : ''}`}
        onClick={openDrawer}
        aria-label={unreadCount > 0 ? `${unreadCount} unread activity` : 'Open activity feed'}
        title={unreadCount > 0 ? `${unreadCount} unread reward${unreadCount === 1 ? '' : 's'}` : 'Recent rewards'}
      >
        <Bell size={16} aria-hidden="true" />
        {unreadCount > 0 && <span className="bell-pip">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      <ActivityDrawer open={open} items={items} onClose={() => setOpen(false)} />
    </>
  );
}

function readLastSeen(): number {
  try {
    const v = Number(localStorage.getItem(SEEN_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}
function writeLastSeen(ts: number): void {
  try { localStorage.setItem(SEEN_KEY, String(ts)); } catch { /* */ }
}
